/**
 * useConfirm — drop-in replacement for window.confirm / window.prompt.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm('Delete this entry?');
 *   if (!ok) return;
 *
 *   // With options:
 *   const ok = await confirm({
 *     title: 'Delete year entry?',
 *     body: 'This cannot be undone.',
 *     confirm: 'Delete',
 *     cancel: 'Keep it',
 *     danger: true,       // red confirm button
 *   });
 *
 *   // Prompt variant (returns string | null):
 *   const value = await confirm({ title: '...', prompt: true, placeholder: 'Type DELETE' });
 */
import { useState, useCallback, useContext, createContext, useRef } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // null = closed
  const resolveRef = useRef(null);

  const confirm = useCallback((msgOrOpts) => {
    const opts = typeof msgOrOpts === 'string' ? { title: msgOrOpts } : msgOrOpts;
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ ...opts, promptValue: '' });
    });
  }, []);

  const close = (result) => {
    setState(null);
    resolveRef.current?.(result);
    resolveRef.current = null;
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <AlertDialog open onOpenChange={(open) => { if (!open) close(false); }}>
          <AlertDialogContent className="max-w-sm rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle>{state.title || 'Are you sure?'}</AlertDialogTitle>
              {state.body && (
                <AlertDialogDescription>{state.body}</AlertDialogDescription>
              )}
            </AlertDialogHeader>

            {state.prompt && (
              <input
                autoFocus
                type="text"
                placeholder={state.placeholder || ''}
                value={state.promptValue}
                onChange={e => setState(s => ({ ...s, promptValue: e.target.value }))}
                className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm mt-1 focus:outline-none focus:border-orange-400"
              />
            )}

            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => close(state.prompt ? null : false)}>
                {state.cancel || 'Cancel'}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => close(state.prompt ? (state.promptValue || '') : true)}
                className={state.danger
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-orange-500 hover:bg-orange-600 text-white'}
              >
                {state.confirm || 'Confirm'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}
